import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import type { Group, Role, User, WriteRejection } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { describeRejection } from "@/modules/settings/errors";
import { useOpenRecord } from "@/routes/useOpenRecord";

const GROUPS_QUERY_KEY = ["settings", "groups"];
const USERS_QUERY_KEY = ["settings", "users"];

/**
 * One group, opened from `GroupsList` in a `RecordDrawer` (FR-ACL-6, M15-3).
 *
 * `users` is the same whole-list read `GroupsList` already makes for its
 * member-count column — `Group` carries no member list of its own, only
 * `roleIds`, so membership is read from `User.groupIds` here exactly as it is
 * counted there.
 */
export function GroupDetail({
  group,
  users,
  roles,
}: {
  group: Group;
  users: User[];
  roles: Role[];
}) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const { close } = useOpenRecord();

  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(group.name);
  const [renameRejection, setRenameRejection] = useState<WriteRejection | null>(
    null,
  );

  const currentMemberIds = users
    .filter((user) => user.groupIds.includes(group.id))
    .map((user) => user.id);

  const [memberDraft, setMemberDraft] = useState<string[] | null>(null);
  const selectedMemberIds = memberDraft ?? currentMemberIds;
  const membersDirty =
    memberDraft !== null &&
    (memberDraft.length !== currentMemberIds.length ||
      memberDraft.some((id) => !currentMemberIds.includes(id)));
  const [membersRejection, setMembersRejection] =
    useState<WriteRejection | null>(null);

  const [roleDraft, setRoleDraft] = useState<string[] | null>(null);
  const selectedRoleIds = roleDraft ?? group.roleIds;
  const rolesDirty =
    roleDraft !== null &&
    (roleDraft.length !== group.roleIds.length ||
      roleDraft.some((id) => !group.roleIds.includes(id)));
  const [rolesRejection, setRolesRejection] = useState<WriteRejection | null>(
    null,
  );

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteRejection, setDeleteRejection] = useState<WriteRejection | null>(
    null,
  );

  function invalidateGroups() {
    void queryClient.invalidateQueries({ queryKey: GROUPS_QUERY_KEY });
  }
  function invalidateUsers() {
    void queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
  }

  const renameMutation = useMutation({
    mutationFn: (name: string) => data.settings.renameGroup(group.id, name),
    onSuccess: (result) => {
      if (!result.ok) {
        setRenameRejection(result.rejection);
        return;
      }
      setRenaming(false);
      setRenameRejection(null);
      invalidateGroups();
    },
  });

  const membersMutation = useMutation({
    mutationFn: (userIds: string[]) =>
      data.settings.setGroupMembers(group.id, userIds),
    onSuccess: (result) => {
      if (!result.ok) {
        setMembersRejection(result.rejection);
        return;
      }
      setMemberDraft(null);
      setMembersRejection(null);
      invalidateGroups();
      invalidateUsers();
    },
  });

  const rolesMutation = useMutation({
    mutationFn: (roleIds: string[]) =>
      data.settings.setGroupRoles(group.id, roleIds),
    onSuccess: (result) => {
      if (!result.ok) {
        setRolesRejection(result.rejection);
        return;
      }
      setRoleDraft(null);
      setRolesRejection(null);
      invalidateGroups();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => data.settings.deleteGroup(group.id),
    onSuccess: (result) => {
      if (!result.ok) {
        setDeleteRejection(result.rejection);
        return;
      }
      setConfirmingDelete(false);
      invalidateGroups();
      invalidateUsers();
      close();
    },
  });

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="space-y-1">
        {renaming ? (
          <form
            className="flex items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              const trimmed = nameDraft.trim();
              if (trimmed === "") return;
              renameMutation.mutate(trimmed);
            }}
          >
            <Input
              autoFocus
              className="h-8"
              value={nameDraft}
              onChange={(event) => {
                setNameDraft(event.target.value);
              }}
            />
            <Button
              type="submit"
              size="sm"
              disabled={nameDraft.trim() === "" || renameMutation.isPending}
            >
              Save
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                setRenaming(false);
                setNameDraft(group.name);
                setRenameRejection(null);
              }}
            >
              Cancel
            </Button>
          </form>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold">{group.name}</h3>
            <Button
              type="button"
              size="icon-sm"
              variant="ghost"
              aria-label="Rename group"
              onClick={() => {
                setNameDraft(group.name);
                setRenaming(true);
              }}
            >
              <Pencil />
            </Button>
          </div>
        )}
        {renameRejection ? (
          <p className="text-destructive text-xs">
            {describeRejection(renameRejection)}
          </p>
        ) : null}
        {group.description ? (
          <p className="text-muted-foreground text-sm">{group.description}</p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label>Members</Label>
        <div className="max-h-64 space-y-2 overflow-auto rounded-md border p-3">
          {users.length === 0 ? (
            <p className="text-muted-foreground text-sm">No users exist yet.</p>
          ) : (
            users.map((user) => (
              <div key={user.id} className="flex items-center gap-2">
                <Checkbox
                  id={`group-${group.id}-member-${user.id}`}
                  checked={selectedMemberIds.includes(user.id)}
                  onCheckedChange={(checked) => {
                    const base = memberDraft ?? currentMemberIds;
                    setMemberDraft(
                      checked
                        ? [...base, user.id]
                        : base.filter((id) => id !== user.id),
                    );
                  }}
                />
                <Label
                  htmlFor={`group-${group.id}-member-${user.id}`}
                  className="text-sm font-normal"
                >
                  {user.name}
                  <span className="text-muted-foreground ml-1">
                    {user.email}
                  </span>
                </Label>
              </div>
            ))
          )}
        </div>
        {membersRejection ? (
          <p className="text-destructive text-xs">
            {describeRejection(membersRejection)}
          </p>
        ) : null}
        {membersDirty ? (
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              disabled={membersMutation.isPending}
              onClick={() => {
                membersMutation.mutate(selectedMemberIds);
              }}
            >
              {membersMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : null}
              Save members
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                setMemberDraft(null);
                setMembersRejection(null);
              }}
            >
              Revert
            </Button>
          </div>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label>Roles</Label>
        <div className="space-y-2 rounded-md border p-3">
          {roles.length === 0 ? (
            <p className="text-muted-foreground text-sm">No roles exist yet.</p>
          ) : (
            roles.map((role) => (
              <div key={role.id} className="flex items-center gap-2">
                <Checkbox
                  id={`group-${group.id}-role-${role.id}`}
                  checked={selectedRoleIds.includes(role.id)}
                  onCheckedChange={(checked) => {
                    const base = roleDraft ?? group.roleIds;
                    setRoleDraft(
                      checked
                        ? [...base, role.id]
                        : base.filter((id) => id !== role.id),
                    );
                  }}
                />
                <Label
                  htmlFor={`group-${group.id}-role-${role.id}`}
                  className="text-sm font-normal"
                >
                  {role.name}
                </Label>
              </div>
            ))
          )}
        </div>
        {rolesRejection ? (
          <p className="text-destructive text-xs">
            {describeRejection(rolesRejection)}
          </p>
        ) : null}
        {rolesDirty ? (
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              disabled={rolesMutation.isPending}
              onClick={() => {
                rolesMutation.mutate(selectedRoleIds);
              }}
            >
              {rolesMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : null}
              Save roles
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                setRoleDraft(null);
                setRolesRejection(null);
              }}
            >
              Revert
            </Button>
          </div>
        ) : null}
      </div>

      <div className="space-y-2 border-t pt-4">
        <Label>Danger zone</Label>
        <div>
          <Button
            type="button"
            size="sm"
            variant="destructive"
            onClick={() => {
              setDeleteRejection(null);
              setConfirmingDelete(true);
            }}
          >
            <Trash2 />
            Delete group
          </Button>
        </div>
      </div>

      <Dialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
        <DialogContent
          title={`Delete ${group.name}?`}
          description="This removes its members' access through it and cannot be undone."
        >
          <div className="space-y-4">
            <p className="text-muted-foreground text-sm">
              Members keep any roles held directly. Any docs folder access
              granted to this group is removed too.
            </p>
            {deleteRejection ? (
              <p className="text-destructive text-sm">
                {describeRejection(deleteRejection)}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </DialogClose>
              <Button
                type="button"
                variant="destructive"
                disabled={deleteMutation.isPending}
                onClick={() => {
                  deleteMutation.mutate();
                }}
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                ) : null}
                Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
